/**
 * PhotoInstallBanner — iOS home-screen install nudge (FT-348 2026-07-08).
 *
 * See DESIGN-BRIEF.md §5.2 for approved copy.
 *
 * Rendered only when `isOpen === true` (parent decides via
 * `usePhotoInstallPrompt`).
 *
 * NOTE: This is a NON-BLOCKING bottom sheet (not a full-screen modal). It
 * slides up from the bottom of the viewport and does NOT cover the whole
 * screen, so the main app content above it (back button, complete button,
 * etc.) stays visible and tappable. A previous version reused the shared
 * `Modal` component (`fixed inset-0 z-50`), which blocked the entire viewport
 * and could trap users until they cleared their web data — hence this rework.
 *
 *   <PhotoInstallBanner
 *     isOpen={install.isModalOpen}
 *     onInstalled={install.markInstalled}
 *     onDismissed={install.markDismissed}
 *   />
 *
 * The buttons ("I've done this" / "Remind me later") map straight onto the
 * hook's `markInstalled` / `markDismissed` actions — both close the sheet
 * and persist the outcome to localStorage. Tapping the backdrop dismisses.
 */

import React from 'react';
import { Smartphone, Share2 } from 'lucide-react';

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onInstalled  Wire to usePhotoInstallPrompt().markInstalled
 * @param {() => void} props.onDismissed  Wire to usePhotoInstallPrompt().markDismissed
 * @param {string} [props.testId]
 */
export function PhotoInstallBanner({
  isOpen,
  onInstalled,
  onDismissed,
  testId = 'photo-install-banner',
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div>
      {/* Semi-transparent backdrop behind the sheet only. Tapping it
          dismisses the banner. Sits below the sheet (z-40 vs z-50). */}
      <div
        className="fixed inset-0 bg-black/40 z-50"
        onClick={onDismissed}
        aria-hidden
      />

      {/* Non-blocking bottom sheet — pinned to the bottom of the viewport,
          leaving the app content above it visible and tappable. Stop click
          propagation so taps inside the sheet don't hit the backdrop. */}
      <div
        data-testid={testId}
        role="dialog"
        aria-label="Save this app to your home screen"
        onClick={(e) => e.stopPropagation()}
        className="fixed bottom-0 left-0 right-0 z-[60] bg-white rounded-t-2xl shadow-2xl p-5 transition-transform"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 text-cyan-600" aria-hidden>
              <Smartphone className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Save this app to your home screen
              </h2>
              <p className="mt-1 text-sm text-gray-700">
                iOS deletes web app data after 7 days without a visit. Install
                to keep your photos safe.
              </p>
            </div>
          </div>

          <ol
            className="list-decimal pl-5 space-y-1 text-sm text-gray-800"
            data-testid={`${testId}-steps`}
          >
            <li>
              Tap the <strong>Share</strong> button{' '}
              <Share2
                className="inline h-4 w-4 align-text-bottom text-cyan-600"
                aria-label="Share"
              />{' '}
              at the bottom
            </li>
            <li>
              Scroll down and tap <strong>Add to Home Screen</strong>
            </li>
            <li>
              Tap <strong>Add</strong> in the top right
            </li>
          </ol>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onDismissed}
              data-testid={`${testId}-remind`}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Remind me later
            </button>
            <button
              type="button"
              onClick={onInstalled}
              data-testid={`${testId}-installed`}
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-cyan-600 rounded hover:bg-cyan-700"
            >
              I've done this
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoInstallBanner;
